import React, { useCallback, useEffect, useState, useRef } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
  IconButton,
  TextField,
  Button,
  Stack,
  InputAdornment,
  Menu,
  MenuItem,
} from '@mui/material';
import { usePepwiseAuthController } from 'Context/contexts/AuthContext';
import { DataPipelineModal } from '../IBPDataPipeline/modal-component';
import { checkRole } from 'utils/roleUtils';
import PageWrapper from 'components/Common/PageWraper';
import { deleteIbpGdo, exportIbpGdoList, fetchIbpGdoList } from 'service/service';
import { FilterIcon } from 'components/CommonIcons/Icons';
import SearchIcon from '@mui/icons-material/Search';
import { ReactComponent as VectorIbpIcon } from '../../assets/Vector-ibp.svg';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import CustomizedTable from 'components/CustomizedTable/CustomizedTable';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import StatusChip from 'components/Table/StatusChip';
import RowTypography from 'components/Table/RowTypography';
import debounce from 'lodash/debounce';
import OriginalFilterDrawer from '../FilterDrawer/FilterDrawer';
import { DEFAULT_FILTERS } from '../FilterDrawer/constants';
import { FilterState } from '../FilterDrawer/types';
import { useHeader } from 'Context/contexts/HeaderTabRightContentContext';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import { TIMEZONE_MAP } from 'utils/common-util';
import { calculateRange } from 'components/Dropdown/DateTimeRangeFilter/useDateTimeRangePicker';
import { GBulkUploadModal } from './GBulkUploadModal';
import { GBDOLineageModal } from './GBDOLineageModal';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { ibpDefaultFields } from './filterConstants';
import DeleteImage from '../../assets/delete-img-modal.svg';
import { fetchIbpAdvancedFilterOptions } from './ibpAdvancedFilters';

const IbpChipList = ({ values }: { values?: string | string[] }) => {
  let items: string[] = [];
  if (Array.isArray(values)) {
    items = values;
  } else if (values) {
    items = [values];
  }

  if (items.length === 0) return <>--</>;

  return (
    <>
      {items.map((item, index) => (
        <RowTypography
          key={`${item}-${index}`}
          title={item}
          sx={{
            display: 'inline-flex',
            width: 'auto',
            backgroundColor: '#bdd9f3',
            color: '#35658A',
            padding: '4px 10px',
            marginRight: '5px',
            borderRadius: '20px',
            whiteSpace: 'nowrap',
          }}
        >
          {item}
        </RowTypography>
      ))}
    </>
  );
};

const sectorsColumnFormatter = ({ row }: any) => <IbpChipList values={row.sectors} />;

const marketsColumnFormatter = ({ row }: any) => <IbpChipList values={row.markets} />;

const detailsSectorsColumnFormatter = ({ row }: any) => (
  <IbpChipList values={row.sectors || row.projectSector} />
);

const GdoStatusCell = ({ row }: { row: any }) => {
  const rawStatus = row.gdo_status;
  const normalizeStatus = (status: string) => {
    const normalizedStatus = status?.toLowerCase();

    if (['abort', 'aborted'].includes(normalizedStatus)) return 'Aborted';
    if (['success', 'successful', 'succeeded'].includes(normalizedStatus)) return 'Successful';
    if (['fail', 'failed'].includes(normalizedStatus)) return 'Failed';
    if (['inprogress', 'in progress'].includes(normalizedStatus)) return 'In Progress';
    if (normalizedStatus === 'delayed') return 'Delayed';
    if (normalizedStatus === 'not started') return 'Not Started';
    if (normalizedStatus?.includes('sla met')) return 'SLA Met';
    if (normalizedStatus?.includes('sla missed')) return 'SLA Missed';

    return status;
  };

  const colorMap: Record<string, string> = {
    Aborted: '#F57600',
    Successful: '#01C008',
    Failed: '#FE2626',
    'In Progress': '#ECB914',
    'Not Started': '#9E9E9E',
    'SLA Met': '#01C008',
    'SLA Missed': '#FE2626',
  };
  const normalized = rawStatus ? normalizeStatus(rawStatus) : '--';
  const style = row.bgColor ?? colorMap[normalized] ?? '#9E9E9E';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 1.5,
        py: 0.5,
        backgroundColor: style,
        color: '#fff',
        fontWeight: 600,
        fontSize: '0.7rem',
        height: '24px',
        borderRadius: '4px',
        minWidth: '70px',
        width: '100%',
        textAlign: 'center',
      }}
    >
      {rawStatus ? normalized.toUpperCase() : '--'}
    </Box>
  );
};

const GdoActionsCell = ({
  row,
  onEdit,
  onDelete,
}: {
  row: any;
  onEdit: (row: any, event: React.MouseEvent<SVGSVGElement>) => void;
  onDelete: (row: any, event: React.MouseEvent<SVGSVGElement>) => void;
}) => (
  <Stack direction="row" spacing={1} justifyContent="center">
    <EditIcon
      aria-label="edit-gdo"
      onClick={(event) => onEdit(row, event)}
      sx={{ fontSize: 18, color: '#009BD8', cursor: 'pointer' }}
    />
    <DeleteIcon
      aria-label="delete-gdo"
      onClick={(event) => onDelete(row, event)}
      sx={{ fontSize: 18, color: '#E53935', cursor: 'pointer' }}
    />
  </Stack>
);

const JobsSuccessCell = ({ row }: { row: any }) => {
  const value = row.successCount;
  return (
    <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {value && Number(value) !== 0 ? (
        <StatusChip displayNumber status="JobsSuccess" value={value} />
      ) : (
        <Box sx={{ textAlign: 'center', color: 'text.primary', minHeight: 30, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>--</Box>
      )}
    </Box>
  );
};

const JobsFailedCell = ({ row }: { row: any }) => {
  const value = row.failedCount;
  return (
    <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {value && Number(value) !== 0 ? (
        <StatusChip displayNumber status="JobsFailed" value={value} />
      ) : (
        <Box sx={{ textAlign: 'center', color: 'text.primary', minHeight: 30, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>--</Box>
      )}
    </Box>
  );
};

const gdoStatusFormatter = ({ row }: any) => (
  <GdoStatusCell row={{ gdo_status: row.slaStatus, bgColor: row.bgColor }} />
);

const createGdoActionsFormatter = (
  onEdit: (row: any, event: React.MouseEvent<SVGSVGElement>) => void,
  onDelete: (row: any, event: React.MouseEvent<SVGSVGElement>) => void,
) => ({ row }: any) => (
  <GdoActionsCell row={row} onEdit={onEdit} onDelete={onDelete} />
);

const CenteredDetailsCell = ({ value }: { value?: any }) => (
  <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
    {value || '--'}
  </Box>
);

const stageNameDetailsFormatter = ({ row }: any) => <CenteredDetailsCell value={row.projectName} />;

const slaTimeDetailsFormatter = () => <CenteredDetailsCell />;

const aliasDetailsFormatter = ({ row }: any) => (
  <CenteredDetailsCell value={row.projectFriendlyName} />
);

const OwnerDetailsCell = ({ row }: { row: any }) => {
  const raw = row.projectOwner;
  if (!raw) {
    return (
      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}>
        --
      </Box>
    );
  }

  const owners = Array.isArray(raw) ? raw : String(raw).split(/[,;]\s*/).filter(Boolean);

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start', justifyContent: 'flex-start', textAlign: 'left' }}>
      {owners.map((owner: any) => {
        const emailMatch = String(owner).match(/([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/i);
        const email = emailMatch ? emailMatch[0] : '';
        let name = String(owner);
        if (email) {
          name = name.replace(email, '').replace(/[()<>]/g, '').trim();
        }

        return (
          <Box
            key={`owner-${String(owner)}`}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              px: '8px',
              py: '8px',
              minHeight: '48px',
              maxWidth: '320px',
              whiteSpace: 'normal',
              overflow: 'visible',
            }}
          >
            <Box sx={{ fontWeight: 600, color: '#0B3A56', fontSize: '0.95rem', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {name || email}
            </Box>
            {email ? (
              <Box sx={{ fontSize: '0.8rem', color: '#6F8A99', mt: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {email}
              </Box>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
};

const ownerDetailsFormatter = ({ row }: any) => <OwnerDetailsCell row={row} />;

const IBPDashboard = () => {
  const [controller] = usePepwiseAuthController();
  const { role } = controller;
  const isPwCUser = checkRole(role, ['PwC_View', 'PwC_Edit']);
  const navigate = useNavigate();
  const { setRightContextContent } = useHeader();

  const [ibpFilterDrawerOpen, setIbpFilterDrawerOpen] = useState(false);
  const [ibpFiltersLocal, setIbpFiltersLocal] = useState<Record<string, string>>({});
  const [ibpDrawerFilters, setIbpDrawerFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [ibpFilterOptions, setIbpFilterOptions] = useState({
    projectName: [] as string[],
    sectors: [] as string[],
    reports: [] as string[],
    markets: [] as string[],
    gdoStatus: [] as string[],
  });
  const [tableData, setTableData] = useState<any[]>([]);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(100);
  const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: '' });
  const [searchText, setSearchText] = useState<string>('');
  const [loadingTable, setLoadingTable] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<any>({ isOpen: false, message: '' });
  const [toast, setToast] = useState<any>({ open: false, message: '' });
  const [bulkUploadModalOpen, setBulkUploadModalOpen] = useState(false);
  const [lineageModalOpen, setLineageModalOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; row: any | null }>({
    open: false,
    row: null,
  });

  // State for maintaining applied filters, search, and date range across auto-refresh
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [appliedSearch, setAppliedSearch] = useState<string>('');
  const [appliedDateRange, setAppliedDateRange] = useState<any>(null);
  const autoRefreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState<number>(5); // minutes

  // Details panel state: selected program and selected stage
  const [selectedProgram, setSelectedProgram] = useState<any | null>(null);
  const [selectedStage, setSelectedStage] = useState<{ status: string; count: number } | null>(null);
  const detailsRef = useRef<HTMLDivElement | null>(null);

  const handleStageClick = (programRow: any, statusName: string, count: number) => {
    setSelectedProgram(programRow);
    setSelectedStage({ status: statusName, count: count });

    setTimeout(() => {
      if (detailsRef.current) {
        try {
          detailsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } catch (err) {
          window.scrollTo({ top: detailsRef.current!.offsetTop - 80, behavior: 'smooth' });
        }
      }
    }, 120);
  };

  const getStageLabelColor = (status?: string) => {
    if (!status) return '#D6365F';
    const normalized = status.toLowerCase();
    if (normalized.includes('missed')) return '#D6365F';
    if (normalized.includes('met')) return '#01C008';
    if (normalized.includes('progress')) return '#ECB914';
    if (normalized.includes('not started') || normalized.includes('scheduled')) return '#9E9E9E';
    return '#D6365F';
  };

  const handleGdoNameClick = (row: any) => {
    const programName = row?.programName || row?.Program_Name || row?.ProgramName || 'GDO';
    const programId = row?.programId || row?.Program_Id || row?.ProgramID || row?.id || '';
    const safeName = encodeURIComponent(programName);
    const safeProgramId = programId ? String(programId) : '';

    // compute currently selected IBP date-range (fall back to today)
    const getIbpDateRangeDates = () => {
      const savedRangeType = sessionStorage.getItem('IBPDateRangeType') || 'today';
      const savedTimeZone = sessionStorage.getItem('IBPDateRangeTimeZone') || 'CST';
      const savedStartDate = sessionStorage.getItem('IBPStartDate');
      const savedEndDate = sessionStorage.getItem('IBPEndDate');
      const ianaTimezone = TIMEZONE_MAP[savedTimeZone] || TIMEZONE_MAP.CST;

      if (savedRangeType === 'custom' && savedStartDate && savedEndDate) {
        const sd = /^\d{4}-\d{2}-\d{2}$/.test(savedStartDate)
          ? moment.tz(savedStartDate, 'YYYY-MM-DD', ianaTimezone)
          : moment(savedStartDate).tz(ianaTimezone);
        const ed = /^\d{4}-\d{2}-\d{2}$/.test(savedEndDate)
          ? moment.tz(savedEndDate, 'YYYY-MM-DD', ianaTimezone)
          : moment(savedEndDate).tz(ianaTimezone);
        return {
          startDate: sd.format('YYYY-MM-DD'),
          endDate: ed.format('YYYY-MM-DD'),
          dateFilter: 'Custom',
        };
      }

      const range = calculateRange(savedRangeType, ianaTimezone);
      if (range?.from && range?.to) {
        return {
          startDate: moment(range.from).tz(ianaTimezone).format('YYYY-MM-DD'),
          endDate: moment(range.to).tz(ianaTimezone).format('YYYY-MM-DD'),
          dateFilter: savedRangeType === 'today' ? 'Today' : savedRangeType,
        };
      }

      const today = moment.tz(ianaTimezone).format('YYYY-MM-DD');
      return { startDate: today, endDate: today, dateFilter: 'Today' };
    };

    const { startDate, endDate, dateFilter } = getIbpDateRangeDates();

    // mark forward navigation
    sessionStorage.setItem('IBPNavDirection', 'forward');

    if (safeProgramId) {
      navigate(`/data-pipeline/ibpDashboard/gdo-details/${safeProgramId}/${safeName}?startingDate=${startDate}&endingDate=${endDate}&dateFilter=${encodeURIComponent(dateFilter)}`);
    } else {
      navigate(`/data-pipeline/ibpDashboard/gdo-details/${safeName}?startingDate=${startDate}&endingDate=${endDate}&dateFilter=${encodeURIComponent(dateFilter)}`);
    }
  };
  
  const handleGdoEditClick = (row: any, event: React.MouseEvent<SVGSVGElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const programName = row?.programName || row?.Program_Name || row?.ProgramName || '';
    const programId = row?.programId || row?.Program_Id || row?.ProgramID || row?.id || '';

    if (!programName || !programId) {
      return;
    }

    navigate(
      `/data-pipeline/ibpDashboard/gdoTabs?tab=0&programID=${encodeURIComponent(
        String(programId),
      )}&programName=${encodeURIComponent(programName)}&isEdited=true`,
      {
        state: tableData.length >= totalElements
          ? { gdoList: { records: tableData, totalRecords: totalElements } }
          : undefined,
      },
    );
  };

  const handleGdoDeleteClick = (row: any, event: React.MouseEvent<SVGSVGElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDeleteDialog({ open: true, row });
  };

  const handleGdoDelete = async () => {
    const row = deleteDialog.row;
    const programId = row?.programId || row?.Program_Id || row?.ProgramID || row?.id || '';
    if (!programId) return;

    const result = await deleteIbpGdo(String(programId));
    const response = result?.data || result;
    if (result?.success === false || result?.statusCode >= 400 || response?.success === false) {
      setToast({ open: true, message: response?.message || result?.message || 'Unable to delete GDO.' });
      return;
    }

    setDeleteDialog({ open: false, row: null });
    setToast({ open: true, message: response?.message || 'Record deleted successfully' });
    await fetchGdoListData();
  };

  const getColumns = () => {
    const StatusChip = ({ row }: any) => {
      const rawStatus = row.gdo_status;

      if (!rawStatus) {
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 1.5,
              py: 0.5,
              backgroundColor: '#9E9E9E',
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.7rem',
              height: '24px',
              borderRadius: '4px',
              minWidth: '70px',
              width: '100%',
              textAlign: 'center',
            }}
          >
            --
          </Box>
        );
      }

      const normalizeStatus = (status: string) => {
        const s = status?.toLowerCase();

        if (['abort', 'aborted'].includes(s)) return 'Aborted';
        if (['success', 'successful', 'succeeded'].includes(s)) return 'Successful';
        if (['fail', 'failed'].includes(s)) return 'Failed';
        if (['inprogress', 'in progress'].includes(s)) return 'In Progress';
        if (['delayed'].includes(s)) return 'Delayed';
        if (['not started'].includes(s)) return 'Not Started';
        if (s?.includes('sla met')) return 'SLA Met';
        if (s?.includes('sla missed')) return 'SLA Missed';

        return status;
      };

      const normalized = normalizeStatus(rawStatus);
      const colorMap: Record<string, string> = {
        Aborted: '#F57600',
        Successful:'#01C008',
        Failed: '#FE2626',
        'In Progress':'#ECB914',
        'Not Started': '#9E9E9E',
        'SLA Met': '#01C008',
        'SLA Missed': '#FE2626',
      };
      const style = row.bgColor ?? colorMap[normalized] ?? '#9E9E9E';

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 1.5,
            py: 0.5,
            backgroundColor: style,
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.7rem',
            height: '24px',
            borderRadius: '4px',
            minWidth: '70px',
            width: '100%',
            textAlign: 'center',
          }}
        >
          {normalized.toUpperCase()}
        </Box>
      );
    };

    const StageSlaFormatter = ({ row }: any) => {
      // Map statusCountArray indices to status labels
      // Index 0 = SLA Missed, 1 = SLA Met, 2 = In Progress, 3 = Not Started
      const statusCountArray = row.statusCountArray || [0, 0, 0, 0];
      const statusLabels = ['SLA Missed', 'SLA Met', 'In Progress', 'Not Started'];
      const statusColors = ['#D6365F', '#01C008', '#ECB914', '#9E9E9E'];

      // Create stages array from statusCountArray, only including non-zero statuses
      const stages = statusCountArray
        .map((count: number, idx: number) => ({
          label: count.toString(),
          color: statusColors[idx],
          status: statusLabels[idx],
        }))
        .filter((s: any) => parseInt(s.label) > 0);

      // compute numeric counts for proportional sizing
      const counts = stages.map((s: any) => {
        const n = Number(s.label);
        return Number.isFinite(n) && n > 0 ? n : 1;
      });

      const getSLAClassName = (label: string) => {
        if (!label) return '';
        if (label.toLowerCase().includes('missed')) return 'color-missed';
        if (label.toLowerCase().includes('met')) return 'color-met';
        if (label.toLowerCase().includes('progress')) return 'color-progress';
        if (label.toLowerCase().includes('scheduled') || label.toLowerCase().includes('not')) return 'color-scheduled';
        return 'color-others';
      };

      if (stages.length === 0) {
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 1.5,
              py: 0.5,
              backgroundColor: '#9E9E9E',
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.7rem',
              height: '24px',
              borderRadius: '4px',
              minWidth: '70px',
              width: '100%',
              textAlign: 'center',
            }}
          >
            --
          </Box>
        );
      }

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              height: 32,
              borderRadius: 1,
              overflow: 'hidden',
              '& .segment': {
                flex: '1 1 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                textTransform: 'uppercase',
                transition: 'flex 0.25s ease, width 0.25s ease',
                whiteSpace: 'nowrap',
                px: 0.5,
                minWidth: 16,
              },
              '&:hover .segment': {
                flex: '1 1 0',
              },
              '& .segment:hover': {
                flex: '2 1 0',
              },
              '& .toggle-label': {
                width: 0,
                marginLeft: 4,
                opacity: 0,
                overflow: 'hidden',
                transition: 'width 0.2s ease, opacity 0.2s ease',
                fontSize: '0.75rem',
                lineHeight: 1,
              },
              '& .segment:hover .toggle-label': {
                width: 'auto',
                opacity: 1,
              },
            }}
          >
            {stages.map((s: any, idx: number) => {
              const count = counts[idx] || 1;
              const bg = s.color || '#9E9E9E';
              const statusName = s.status || '';

              return (
                <Box
                  key={idx}
                  className={`segment ${getSLAClassName(statusName)}`}
                  sx={{ backgroundColor: bg, color: '#fff', cursor: 'pointer' }}
                  onClick={(e: any) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // show details for this program and status
                    handleStageClick(row, statusName, Number(s.label || 0));
                  }}
                >
                  <Box component="span">{count}</Box>
                  <Box component="span" className="toggle-label">{statusName.toUpperCase()}</Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      );
    };

    return [
      { key: 'programName', name: 'GDO NAME', minWidth: 300, formatter: ({ row }: any) => {
        const name = row.programName || row.Program_Name || row.ProgramName || '--';
        return (
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', cursor: 'pointer' }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleGdoNameClick(row);
            }}
          >
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', pr: 1, color: '#009BD8', textDecoration: 'underline' }}>
              {name}
            </Box>
            {(row.programName || row.Program_Name || row.ProgramName) ? (
              <VectorIbpIcon style={{ width: 18, height: 18, flexShrink: 0 }} />
            ) : null}
          </Box>
        );
      }, resizable: true, sortable: true },
      {
        key: 'sectors',
        name: 'SECTORS',
        minWidth: 150,
        maxWidth: 450,
        formatter: sectorsColumnFormatter,
        resizable: true,
        sortable: true,
      },
      {
        key: 'markets',
        name: 'MARKETS',
        minWidth: 150,
        maxWidth: 450,
        formatter: marketsColumnFormatter,
        resizable: true,
        sortable: true,
      },
      { key: 'actExecutionTime', name: 'ACTUAL EXECUTION TIME (CST)', width: 250, formatter: ({ row }: any) => row.actExecutionTime || '--', resizable: true, sortable: true },
      { key: 'nxtscheduletime', name: 'NEXT SCHEDULED TIME (CST)', width: 250, formatter: ({ row }: any) => row.nxtscheduletime || '--', resizable: true, sortable: true },
      { key: 'statusCountArray', name: 'STAGES SLAs', width: 300, formatter: StageSlaFormatter, resizable: true, sortable: false },
      { key: 'slaStatus', name: 'GDO STATUS', width: 180, formatter: gdoStatusFormatter, resizable: true, sortable: true, cellClass: 'status-center' },
      { key: 'gdoDate', name: 'GDO DATE', width: 150, formatter: ({ row }: any) => row.gdoDate || '--', resizable: true, sortable: true },
      { key: 'programDescription', name: 'DESCRIPTION', minWidth: 250, formatter: ({ row }: any) => row.programDescription || '--', resizable: true, sortable: false },
      { key: 'actions', name: 'ACTIONS', width: 120, formatter: createGdoActionsFormatter(handleGdoEditClick, handleGdoDeleteClick), resizable: false, sortable: false },
    ];
  };

  const handleFilter = (e: any) => {
    console.log('FILTER', e);
  };

  const handleSearch = (e: any) => {
    console.log('FILTER', e);
  };

  const handleSorting = (sortingInfo: any) => {
    // Not used
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    // Not used
  };

  const getToastData = (data: any) => {
    setToast({
      ...toast,
      message: data.data.message,
    });
  };

  const handleClose = () => {
    setToast({
      ...toast,
      open: false,
    });
  };

  const handlepageSizeChange = (e: any) => {
    // Not used
  };

  const onCreateGDOClick = () => {
    navigate(`/data-pipeline/ibpDashboard/gdoTabs`);
  };

  const handleDetailsThreeDotsClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDetailsThreeDotsClose = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleShowLineage = (e?: any) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setLineageModalOpen(true);
  };

  const handleMarketSelect = (market: string) => {
    setLineageModalOpen(false);
    navigate(`/data-pipeline/ibpDashboard/lineage/${market}`);
  };


  const handleOpenIbpFilters = (e?: any) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIbpFilterDrawerOpen(true);
  };

  const mapToOptions = (items: string[] = []) => {
    const unique = [...new Set(items.filter(Boolean))];
    return unique.map((item) => ({ label: item, value: item }));
  };

  const loadIbpFilterOptions = async () => {
    const filters = await fetchIbpAdvancedFilterOptions({ dateRange: getIbpDateRange() });
    setIbpFilterOptions(filters);
  };

  useEffect(() => {
    loadIbpFilterOptions();
  }, []);

  useEffect(() => {
    setRightContextContent(
      <Box display="flex" alignItems="center">
        <IconButton onClick={handleOpenIbpFilters} aria-label="open-filters" sx={{ p: 0 }}>
          <FilterIcon />
        </IconButton>
      </Box>,
    );

    return () => {
      setRightContextContent(null);
    };
  }, [setRightContextContent, handleOpenIbpFilters]);

  const handleCloseIbpFilters = () => {
    setIbpFilterDrawerOpen(false);
  };

  const handleIbpFilterChange = (values: Record<string, string>) => {
    setIbpFiltersLocal(values);
  };

  const handleIbpApply = () => {
    // Apply IBP filters - update applied filters and reset page to 1
    setAppliedFilters(ibpDrawerFilters);
    setPage(1);
    setIbpFilterDrawerOpen(false);
  };

  const handleIbpReset = () => {
    setIbpFiltersLocal({});
    setIbpDrawerFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setPage(1);
  };

  const getIbpDateRange = () => {
    const savedRangeType = sessionStorage.getItem('IBPDateRangeType') || 'today';
    const savedTimeZone = sessionStorage.getItem('IBPDateRangeTimeZone') || 'CST';
    const startDate = sessionStorage.getItem('IBPStartDate');
    const endDate = sessionStorage.getItem('IBPEndDate');
    const ianaTimezone = TIMEZONE_MAP[savedTimeZone] || TIMEZONE_MAP.CST;

    const dateRange: any = {
      dateTimeRangeType: savedRangeType,
      timeZone: savedTimeZone,
    };

    if (savedRangeType === 'custom' && startDate && endDate) {
      if (/^\d{4}-\d{2}-\d{2}$/.test(startDate)) {
        dateRange.startTime = moment.tz(startDate, 'YYYY-MM-DD', ianaTimezone).startOf('day').format('DD-MM-YYYY HH:mm:ss');
      } else {
        dateRange.startTime = moment(startDate).tz(ianaTimezone).format('DD-MM-YYYY HH:mm:ss');
      }
      if (/^\d{4}-\d{2}-\d{2}$/.test(endDate)) {
        dateRange.endTime = moment.tz(endDate, 'YYYY-MM-DD', ianaTimezone).endOf('day').format('DD-MM-YYYY HH:mm:ss');
      } else {
        dateRange.endTime = moment(endDate).tz(ianaTimezone).format('DD-MM-YYYY HH:mm:ss');
      }
    } else {
      const range = calculateRange(savedRangeType, ianaTimezone);
      if (range?.from && range?.to) {
        dateRange.startTime = moment(range.from).tz(ianaTimezone).format('DD-MM-YYYY HH:mm:ss');
        dateRange.endTime = savedRangeType === 'today'
          ? moment.tz(ianaTimezone).endOf('day').format('DD-MM-YYYY HH:mm:ss')
          : moment(range.to).tz(ianaTimezone).format('DD-MM-YYYY HH:mm:ss');
      } else {
        dateRange.dateTimeRangeType = 'today';
        dateRange.startTime = moment.tz(ianaTimezone).startOf('day').format('DD-MM-YYYY HH:mm:ss');
        dateRange.endTime = moment.tz(ianaTimezone).endOf('day').format('DD-MM-YYYY HH:mm:ss');
      }
    }

    return dateRange;
  };

  const buildPayload = () => {
    // Map filter fields to API field names
    const globalFilters: any = {};

    if (appliedFilters.sector && appliedFilters.sector.length > 0) {
      const sectorValues = Array.isArray(appliedFilters.sector) ? appliedFilters.sector : [appliedFilters.sector];
      const cleanSectors = sectorValues
        .filter((s: any) => s && s.value && s.value !== 'all')
        .map((s: any) => s.value || s);
      if (cleanSectors.length > 0) {
        globalFilters.sectors = cleanSectors;
      }
    }

    if (appliedFilters.market && appliedFilters.market.length > 0) {
      const marketValues = Array.isArray(appliedFilters.market) ? appliedFilters.market : [appliedFilters.market];
      const cleanMarkets = marketValues
        .filter((m: any) => m && m.value && m.value !== 'all')
        .map((m: any) => m.value || m);
      if (cleanMarkets.length > 0) {
        globalFilters.markets = cleanMarkets;
      }
    }

    if (appliedFilters.reports && appliedFilters.reports.length > 0) {
      const programValues = Array.isArray(appliedFilters.reports) ? appliedFilters.reports : [appliedFilters.reports];
      const cleanPrograms = programValues
        .filter((p: any) => p && p.value && p.value !== 'all')
        .map((p: any) => p.value || p);
      if (cleanPrograms.length > 0) {
        globalFilters.reports = cleanPrograms;
      }
    }

    if (appliedFilters.gdoStatus && appliedFilters.gdoStatus.length > 0) {
      const statusValues = Array.isArray(appliedFilters.gdoStatus) ? appliedFilters.gdoStatus : [appliedFilters.gdoStatus];
      const cleanStatus = statusValues
        .filter((st: any) => st && st.value && st.value !== 'all')
        .map((st: any) => st.value || st);
      if (cleanStatus.length > 0) {
        globalFilters.gdoStatus = cleanStatus;
      }
    }

    if (appliedFilters.stageName && appliedFilters.stageName.length > 0) {
      const stageValues = Array.isArray(appliedFilters.stageName) ? appliedFilters.stageName : [appliedFilters.stageName];
      const cleanStages = stageValues
        .filter((s: any) => s && s.value && s.value !== 'all')
        .map((s: any) => s.value || s);
      if (cleanStages.length > 0) {
        // UI shows this filter as "Stage name" but backend sent as projectName
        globalFilters.projectName = cleanStages;
      }
    }

    const dateRange = appliedDateRange || getIbpDateRange();

    return {
      paginationInfo: {
        page,
        pageSize,
      },
      searchText: appliedSearch,
      sortingInfo: {
        sortBy: sortingInfo.sortBy || 'programName',
        sortDir: sortingInfo.sortDir || 'asc',
      },
      globalFilters,
      dateRange,
    };
  };

  const fetchGdoListData = async () => {
    setLoadingTable(true);
    try {
      const payload = buildPayload();
      const result = await fetchIbpGdoList(payload);
      const data = result?.data || result || {};

      if (data.records && Array.isArray(data.records)) {
        setTableData(data.records);
        setTotalElements(data.totalRecords || 0);
      } else {
        setTableData([]);
        setTotalElements(0);
      }
    } catch (error) {
      console.error('Failed to fetch GDO list', error);
      setTableData([]);
      setTotalElements(0);
    } finally {
      setLoadingTable(false);
    }
  };

  const handleExportGdoList = async () => {
    try {
      const payload = buildPayload();
      await exportIbpGdoList(payload);
    } catch (error) {
      console.error('Failed to export GDO list', error);
    }
  };

  // Debounced search - keep the same debounced fn across renders
  const debouncedSearchRef = useRef<any>(null);
  if (!debouncedSearchRef.current) {
    debouncedSearchRef.current = debounce((val: string) => {
      setAppliedSearch(val);
      setPage(1);
    }, 300);
  }

  // cancel pending debounced calls on unmount
  useEffect(() => {
    return () => {
      if (debouncedSearchRef.current && debouncedSearchRef.current.cancel) {
        debouncedSearchRef.current.cancel();
      }
    };
  }, []);

  // Handle date range selection
  const handleDateRangeChange = (dateRange: any) => {
    setAppliedDateRange(dateRange);
    setPage(1);
  };

  // Watch for changes in sessionStorage date range (from date range picker OK button)
  useEffect(() => {
// Listen for IBP date range updates from the header and storage
	const handleDateRangeApplied = (event?: Event) => {
		const currentDateRange = getIbpDateRange();
		setAppliedDateRange(currentDateRange);
		setPage(1);
	};

	// Listen for custom event dispatched by date range picker
	window.addEventListener('IBPDateRangeApplied', handleDateRangeApplied);
	window.addEventListener('ibp-session-storage-updated', handleDateRangeApplied);

    // Also listen for storage changes (for cross-tab/window updates)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key && (e.key.includes('IBPDateRange') || e.key.includes('IBPStartDate') || e.key.includes('IBPEndDate'))) {
        handleDateRangeApplied();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('IBPDateRangeApplied', handleDateRangeApplied);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Auto-refresh effect
  useEffect(() => {
    // First fetch when dependencies change
    fetchGdoListData();

    // Set up auto-refresh timer (every 5 minutes)
    if (autoRefreshTimerRef.current) {
      clearInterval(autoRefreshTimerRef.current);
    }

    autoRefreshTimerRef.current = setInterval(() => {
      fetchGdoListData();
    }, autoRefreshInterval * 60 * 1000);

    return () => {
      if (autoRefreshTimerRef.current) {
        clearInterval(autoRefreshTimerRef.current);
      }
    };
  }, [page, pageSize, sortingInfo, appliedSearch, appliedFilters, appliedDateRange, autoRefreshInterval]);

  // Dropdown state for CREATE GDO menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleDropdownClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDropdownClose = () => {
    setAnchorEl(null);
  };

  const handleProjectSLA = (programData: any, filterType: string) => {
    // Not used
  };

  const onClickAddProject = (event: any, prgId: string, prgName: string) => {
    // Not used
  };

  const handleEditClose = useCallback((closeType: any) => {
    setOpenModal({
      isOpen: false,
      closeType: closeType,
    });
  }, []);

  const handleAction = (module: string, type: string, data: any, e: any) => {
    e.preventDefault();
    e.stopPropagation();
    handleDetailsThreeDotsClose(e);
    data = {
      ...data,
      ProgramName: data.Program_Name,
      ProgramID: data.Program_id,
    };
    switch (type) {
      case 'edit':
        setOpenModal({
          isOpen: true,
          type: module,
          action: 'EDIT',
          data: data,
        });
        navigate(
          `/data-pipeline/programs-tab?programID=${data.Program_id}&programName=${data.Program_Name}&isEdited=true`,
        );
        break;
      case 'delete':
        setOpenModal({
          isOpen: true,
          type: module,
          action: 'DELETE',
          data: data,
        });
        break;
      default:
        break;
    }
  };

  const getProgramData = useCallback(async () => {
    // Not fetching data - page displays blank content only
  }, []);

  useEffect(() => {
    // No data fetching needed
  }, []);

  const getSectorData = async () => {
    // Not used
  };

  const getProjectsByProgram = async (
    data: any,
    page: number,
    pageSize: number,
    filterType?: string,
  ) => {
    // Not used
  };

  type Program = {
    [key: string]: any;
  };

  type ProgramList = {
    [sector: string]: Program[];
  };

  const clearSessionData = () => {
    sessionStorage.removeItem('executiveTemplateTab');
    sessionStorage.removeItem('executiveTemplateDashboard');
    sessionStorage.removeItem('executiveTemplateWorkflow');
    sessionStorage.removeItem('executiveTemplateHops');
    sessionStorage.removeItem('executiveTemplateSla');
  };

  const handleExecutiveTemplate = () => {
    handleDropdownClose();
    clearSessionData();
    navigate(`/data-pipeline/executiveTemplate`);
  };

  const detailsColumns = [
    {
      key: 'sectors',
      name: 'SECTORS',
      formatter: detailsSectorsColumnFormatter,
      minWidth: 180,
    },
    {
      key: 'projectName',
      name: 'STAGE NAME',
      formatter: stageNameDetailsFormatter,
      minWidth: 300,
    },
    {
      key: 'failedCount',
      name: 'JOBS FAILED',
      formatter: JobsFailedCell,
      minWidth: 120,
    },
    {
      key: 'successCount',
      name: 'JOBS SUCCESS',
      formatter: JobsSuccessCell,
      minWidth: 120,
    },
    {
      key: 'slaTime',
      name: 'SLA TIME (CST)',
      formatter: slaTimeDetailsFormatter,
      minWidth: 150,
    },
    {
      key: 'projectFriendlyName',
      name: 'ALIAS',
      formatter: aliasDetailsFormatter,
      minWidth: 180,
    },
    {
      key: 'projectOwner',
      name: 'OWNER',
      formatter: ownerDetailsFormatter,
      minWidth: 220,
    },
  ];

  return (
    !isPwCUser && <PageWrapper>
      <Box className="main-bordered-container" bgcolor="backgroundLight.primary">

        {/* Details table (mock data for now) */}
        <Box sx={{ backgroundColor: 'white', p: 2, borderRadius: 1 }}>
          <CustomizedTable
            tableTitle="All GDOs"
            tableData={tableData}
            total={totalElements}
            page={page}
            pageSize={pageSize}
            onPageChange={(p) => setPage(p)}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
            onSorting={(s) => setSortingInfo(s)}
            sortingInfo={sortingInfo}
            defaultColumns={getColumns().map((c:any) => c.name)}
            tableType="ibp-gdo-details"
            columns={getColumns()}
            hasGroupBy={false}
            isChildren={true}
            isFetching={loadingTable}
            rowHeight={44}
            pageSizeOptions={[100, 200, 500, 1000]}
            showExport={true}
            exportTableData={handleExportGdoList}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', flexWrap: 'nowrap', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<VectorIbpIcon style={{ width: 18, height: 18 }} />}
                onClick={handleShowLineage}
                sx={{
                  borderColor: '#009BD8',
                  color: '#009BD8',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.82rem',
                  borderRadius: '4px',
                  px: 1.5,
                  minWidth: 150,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  '& .MuiButton-startIcon, & .MuiButton-label': {
                    whiteSpace: 'nowrap'
                  },
                  '&:hover': { borderColor: '#007ea8' }
                }}
              >
                Show GDO Lineage
              </Button>
              <TextField
                size="small"
                placeholder="Search by GDO Name or Status"
                value={searchText}
                onChange={(e) => {
                  const v = e.target.value;
                  setSearchText(v);
                  if (debouncedSearchRef.current) debouncedSearchRef.current(v);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#6F8A99' }} />
                    </InputAdornment>
                  ),
                  sx: {
                    height: 36,
                  }
                }}
                inputProps={{
                  style: { color: '#0B3A56' }
                }}
                sx={{
                  width: { xs: '200px', sm: '280px', md: '320px' },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '4px',
                    backgroundColor: '#F6FBFD',
                    paddingRight: '4px',
                    '& fieldset': {
                      borderColor: '#6F8A99'
                    },
                    '&:hover fieldset': {
                      borderColor: '#6F8A99'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#6F8A99'
                    }
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#6F8A99',
                    opacity: 1
                  }
                }}
              />
              <Button
                variant="text"
                onClick={() => setBulkUploadModalOpen(true)}
                sx={{
                  borderColor: '#009BD8',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  color: '#009BD8',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.82rem',
                  borderRadius: '4px',
                  px: 1.5,
                  minWidth: 150,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  '& .MuiButton-startIcon, & .MuiButton-label': {
                    whiteSpace: 'nowrap'
                  },
                  '&:hover': { borderColor: '#007ea8' }
                }}
                startIcon={
                  <CloudUploadIcon style={{ width: '18px', height: '18px' }} color="action" />
                }
              >
                BULK UPLOAD GDOs
              </Button>
              <Button
                disabled={true}
                variant="contained"
                onClick={handleDropdownClick}
                endIcon={<ArrowDropDownIcon />}
                sx={{
                  backgroundColor: '#009BD8',
                  color: '#ffffff',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.82rem',
                  borderRadius: '4px',
                  px: 2,
                  minWidth: 150,
                  height: 36,
                  '&:hover': {
                    backgroundColor: '#007ea8',
                  },
                  '&.Mui-disabled': {
                    color: '#000 !important',
                    cursor: 'not-allowed',
                  }
                }}
              >
                CREATE GDO
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleDropdownClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                MenuListProps={{ sx: { backgroundColor: '#009BD8 !important' } }}
                slotProps={{ paper: { sx: { borderRadius: '4px', color: '#fff', boxShadow: '0px 4px 16px rgba(0,0,0,0.3)', mt: 0 } } }}
              >
                <MenuItem
                  onClick={() => {
                    handleDropdownClose();
                    onCreateGDOClick();
                  }}
                  sx={{ color: '#fff', borderRadius: '4px', textTransform: 'uppercase', fontFamily: 'Montserrat', '&:hover': { backgroundColor: '#005b99 !important', color: '#fff' } }}
                >
                  <Button variant="pepwiseButtonText">CREATE GDO</Button>
                </MenuItem>
                <MenuItem sx={{ color: '#fff', borderRadius: '4px', textTransform: 'uppercase', fontFamily: 'Montserrat', '&:hover': { backgroundColor: '#005b99 !important', color: '#fff' } }}>
                  <Button variant="pepwiseButtonText" disabled>EXECUTIVE TEMPLATE</Button>
                </MenuItem>
              </Menu>
            </Stack>
          </CustomizedTable>
        </Box>

        {selectedProgram && (
          <Box ref={detailsRef} sx={{ backgroundColor: 'white', p: 2, borderRadius: 1, mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ fontSize: '1rem', fontWeight: 700, color: 'text.primary' }}>
                {selectedProgram?.programName || selectedProgram?.Program_Name || selectedProgram?.ProgramName || '--'}
                <Box
                  component="span"
                  sx={{
                    ml: 2,
                    color: getStageLabelColor(selectedStage?.status),
                    fontWeight: 700,
                    fontSize: '0.9rem',
                  }}
                >
                  {selectedStage?.count} {selectedStage?.status}
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                '& .rdg-row .rdg-cell, & .rdg-header-row .rdg-cell': {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  padding: '0 8px',
                },
                '& .rdg-row .rdg-cell > div': {
                  justifyContent: 'flex-start !important',
                  textAlign: 'left !important',
                },
                '& .rdg-header-row .rdg-cell': {
                  borderInlineEnd: '1px solid #fff !important',
                  borderBlockEnd: '1px solid #fff !important',
                },
                '& .rdg-row .rdg-cell': {
                  borderInlineEnd: '1px solid #fff !important',
                  borderBlockEnd: '1px solid #fff !important',
                },
                '& .rdg-row': {
                  borderBlockEnd: '1px solid #fff !important',
                },
              }}
            >
              <CustomizedDataGrid
                tableColumns={detailsColumns}
                tableData={selectedProgram.projects || []}
                tableType="ibp-details-grid"
                defaultColumnNames={detailsColumns.map((c:any) => c.name)}
                page={1}
                pageSize={100}
                isFetching={false}
                isRenderPagination={false}
                isShowMoreButton={false}
                rowHeight={68}
                headerRowHeight={40}
                onCellClick={() => {}}
              />
            </Box>
          </Box>
        )}

        <Dialog open={openModal.isOpen} onClose={handleEditClose} fullWidth={true} maxWidth="md">
          <DataPipelineModal
            handleEditClose={handleEditClose}
            programData={openModal}
            getToastData={getToastData}
          />
        </Dialog>
        <Dialog
          open={deleteDialog.open}
          onClose={() => setDeleteDialog({ open: false, row: null })}
          fullWidth
          maxWidth="sm"
          PaperProps={{ sx: { borderRadius: '4px', overflow: 'hidden', backgroundColor: '#F9FCFF' } }}
        >
          <DialogTitle sx={{ backgroundColor: '#003771', color: '#FFFFFF', fontSize: '16px', fontWeight: 600, py: 1.25 }}>
            Deleting GDO
            <IconButton
              aria-label="Close delete confirmation"
              onClick={() => setDeleteDialog({ open: false, row: null })}
              sx={{ color: '#FFFFFF', float: 'right', p: 0 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ px: 4, py: 3, backgroundColor: '#F9FCFF' }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box component="img" src={DeleteImage} alt="" sx={{ width: 64, height: 64 }} />
              <Stack spacing={1}>
                <Box sx={{ color: '#111111', fontSize: '20px', fontWeight: 600 }}>
                  Are you sure to delete GDO {deleteDialog.row?.programName || deleteDialog.row?.Program_Name || deleteDialog.row?.ProgramName}?
                </Box>
                <Box sx={{ color: '#333333', fontSize: '14px' }}>Once confirmed, the action can&apos;t be undone.</Box>
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 4, pb: 3, backgroundColor: '#F9FCFF' }}>
            <Button onClick={() => setDeleteDialog({ open: false, row: null })} sx={{ color: '#009BD8', fontWeight: 700 }}>
              NO, I WANT IT!
            </Button>
            <Button onClick={handleGdoDelete} variant="contained" sx={{ backgroundColor: '#FF2B2B', color: '#FFFFFF', fontWeight: 700, '&:hover': { backgroundColor: '#D92121' } }}>
              DELETE
            </Button>
          </DialogActions>
        </Dialog>
        <Snackbar
          open={toast.open}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          autoHideDuration={6000}
          onClick={handleClose}
        >
          <Alert onClose={handleClose} severity="success" variant="filled">{toast.message}</Alert>
        </Snackbar>
        <GBulkUploadModal
          open={bulkUploadModalOpen}
          onClose={() => {
            setBulkUploadModalOpen(false)
            fetchGdoListData()
            loadIbpFilterOptions()
          }}
        />
        <GBDOLineageModal
          open={lineageModalOpen}
          onClose={() => setLineageModalOpen(false)}
          onSelectMarket={handleMarketSelect}
        />
        <OriginalFilterDrawer
          open={ibpFilterDrawerOpen}
          onClose={handleCloseIbpFilters}
          onApply={(filters: FilterState) => {
            // When user applies drawer filters, update applied filters and fetch data
            setIbpDrawerFilters(filters);
            setAppliedFilters(filters); // This triggers useEffect to fetch data
            setPage(1); // Reset pagination
            setIbpFilterDrawerOpen(false);
          }}
          currentFilters={ibpDrawerFilters}
          filterFields={[
            { label: 'Sectors', placeholder: 'Select sectors...', options: [{ label: 'All', value: 'all' }, ...mapToOptions(ibpFilterOptions.sectors)], value: ibpDrawerFilters.sector, field: 'sector' },
            { label: 'Markets', placeholder: 'Select markets...', options: [{ label: 'All', value: 'all' }, ...mapToOptions(ibpFilterOptions.markets)], value: ibpDrawerFilters.market || [], field: 'market' },
            { label: 'Reports', placeholder: 'Select reports...', options: [{ label: 'All', value: 'all' }, ...mapToOptions(ibpFilterOptions.reports)], value: ibpDrawerFilters.reports, field: 'reports' },
            { label: 'GDO Status', placeholder: 'Select status...', options: [{ label: 'All', value: 'all' }, ...mapToOptions(ibpFilterOptions.gdoStatus)], value: ibpDrawerFilters.gdoStatus || [], field: 'gdoStatus' },
            { label: 'Stage name', placeholder: 'Select stage...', options: [{ label: 'All', value: 'all' }, ...mapToOptions(ibpFilterOptions.projectName)], value: ibpDrawerFilters.stageName || [], field: 'stageName' }
          ] as any}
          customFilterFields={[]}
          hideAdditionalFilters={true}
          hideMoreFilters={true}
          ibpPrimaryFields={ibpDefaultFields}
        />
      </Box>
    </PageWrapper>
  );
};

export default IBPDashboard;
